/**
 * Returns true if the given value resolves to a Saturday or Sunday.
 * Accepts Date objects, ISO strings, and nullish values (which return false).
 */
export function isWeekendDate(value: Date | string | null | undefined): boolean {
    if (!value) return false;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
}

/**
 * Format a date to YYYY-MM-DD HH:mm format
 * @param date - Date object, string, or null
 * @returns Formatted date string or empty string if date is null/invalid
 */
export function formatDate(date: Date | string | null | undefined): string {
    if (!date) return '';

    const d = new Date(date);
    const year = d.getFullYear();
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let hour = '' + d.getHours();
    let minutes = '' + d.getMinutes();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    if (hour.length < 2) hour = '0' + hour;
    if (minutes.length < 2) minutes = '0' + minutes;

    return `${year}-${month}-${day} ${hour}:${minutes}`;
}

/**
 * Format a date to a localized string
 * @param isoString - ISO date string or null
 * @param justDate
 * @returns Formatted date string or "Not Set" if null
 */
export function formatDateLocale(isoString: string | null | undefined, justDate = false): string {
    if (!isoString) return 'Not Set';

    const date = new Date(isoString);
    if (justDate) {
        return date.toLocaleDateString('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    }

    return date.toLocaleString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

/**
 * Format a date to YYYY-MM-DD format (date only, no time)
 */
export function formatDateOnly(date: Date | string | null | undefined): string {
    if (!date) return '';

    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Convert value to Date object
 */
export function toDate(value: Date | string): Date {
    return value instanceof Date ? value : new Date(value);
}

/**
 * Check if two dates have changed
 */
export function dateChanged(current: Date | string | null, previous: Date | string | null): boolean {
    if (!current) return false;
    if (!previous) return true;
    return toDate(current).getTime() !== toDate(previous).getTime();
}

/**
 * Add duration (in days) to a start date
 * Handles fractional days and DST correctly
 */
export function addDaysToDate(startDate: Date, durationDays: number): Date {
    const wholeDays = Math.floor(durationDays);
    const fractionalDay = durationDays - wholeDays;

    const result = new Date(startDate);
    result.setDate(result.getDate() + wholeDays);

    if (fractionalDay > 0) {
        const fractionalMs = fractionalDay * 24 * 60 * 60 * 1000;
        result.setTime(result.getTime() + fractionalMs);
    }

    return result;
}

/**
 * Calculate days between two dates
 * For midnight dates: returns calendar days (ignores DST transitions)
 * For other times: returns precise elapsed time rounded to 2 decimal places
 */
export function daysBetween(startDate: Date, endDate: Date): number {
    // Check if both dates are at midnight (00:00:00)
    const startAtMidnight = startDate.getHours() === 0 && startDate.getMinutes() === 0 && startDate.getSeconds() === 0;
    const endAtMidnight = endDate.getHours() === 0 && endDate.getMinutes() === 0 && endDate.getSeconds() === 0;

    // If both are at midnight, count calendar days (avoids DST issues)
    if (startAtMidnight && endAtMidnight) {
        // Create date-only objects at local midnight
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const diffMs = endDateOnly.getTime() - startDateOnly.getTime();
        return Math.round(diffMs / (1000 * 60 * 60 * 24));
    }

    // Otherwise, calculate precise elapsed time (for fractional days with times)
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return Math.round(diffDays * 100) / 100;
}

/**
 * State tracker for date calculations (prevents circular updates)
 */
export type DateCalculationState = {
    prevStartDate: Date | null;
    prevEndDate: Date | null;
    prevDuration: string;
};

/**
 * Calculate end date from start date and duration
 * Returns null if calculation shouldn't proceed, or the new end date and updated state
 */
export function calculateEndDate(
    startDate: Date | string | null | undefined,
    duration: string | null | undefined,
    state: DateCalculationState,
): { endDate: Date; newState: DateCalculationState } | null {
    if (!startDate || !duration || parseFloat(duration) <= 0) return null;

    const startChanged = dateChanged(startDate, state.prevStartDate);
    const durationChanged = duration !== state.prevDuration;

    if (!startChanged && !durationChanged) return null;

    const start = toDate(startDate);
    const end = addDaysToDate(start, parseFloat(duration));

    return {
        endDate: end,
        newState: {
            prevStartDate: new Date(start),
            prevDuration: duration,
            prevEndDate: new Date(end),
        },
    };
}

/**
 * Calculate duration from start date and end date
 * Returns null if calculation shouldn't proceed, or the new duration and updated state
 */
export function calculateDuration(
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined,
    state: DateCalculationState,
): { duration: string; newState: DateCalculationState } | null {
    if (!startDate || !endDate) return null;

    const endChanged = dateChanged(endDate, state.prevEndDate);
    if (!endChanged) return null;

    const start = toDate(startDate);
    const end = toDate(endDate);

    // console.log({start, end});
    const days = daysBetween(start, end);

    if (days < 0) return null;

    return {
        duration: days.toString(),
        newState: {
            prevStartDate: new Date(start),
            prevEndDate: new Date(end),
            prevDuration: days.toString(),
        },
    };
}
